import { Component } from '@theme/component';
import '@theme/fly-to-cart';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {MutationObserver | null} */
  #priceStyleObserver = null;

  /** @type {number | null} */
  #priceStyleFrame = null;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
    this.#observeProductPriceStyles();

    // IntersectionObserver callbacks gate visibility on #isChatActive(), but
    // if the shopper scrolls before the Inbox bundle has upgraded
    // <shopify-chat>, the bar shows and nothing re-runs that check. Hide it
    // once the element is defined so the bar doesn't overlap the chat UI.
    customElements.whenDefined('shopify-chat').then(() => {
      if (signal.aborted) return;
      if (this.#isStuck && this.#isChatActive()) this.#hideStickyBar();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    this.#priceStyleObserver?.disconnect();
    if (this.#priceStyleFrame !== null) {
      cancelAnimationFrame(this.#priceStyleFrame);
    }
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          if (this.#isChatActive()) return;
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            if (!this.#isChatActive()) {
              this.#showStickyBar();
            }
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    // The product section can be morphed after a variant selection, so resolve
    // the live button and quantity input at click time instead of trusting an
    // earlier cached reference.
    const productForm = this.#getProductForm();
    this.#targetAddToCartButton = productForm?.querySelector('[ref="addToCartButton"]') ?? null;
    if (!this.#targetAddToCartButton) return;

    this.#syncSelectorValue(this.#getMainQuantitySelector(), this.#currentQuantity);
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = true;
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles product select events (variant selected and updated)
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // Update variant ID from the event detail (variant:selected part)
    const { optionValueId } = event.detail ?? {};
    if (optionValueId) {
      this.dataset.currentVariantId = optionValueId;
    }

    // Wait for the promise to resolve with variant update data. Third-party
    // variant pickers can dispatch a compatible event without a promise; ignore
    // those safely instead of throwing in the console.
    if (!(event.promise instanceof Promise)) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, productId, resource: variant } = detail;

        if (productId && productId !== this.dataset.productId) return;

        // Get the new sticky add to cart HTML from the server response
        const newStickyAddToCart = /** @type {HTMLElement | null} */ (html.querySelector('sticky-add-to-cart'));
        if (!newStickyAddToCart) return;

        const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) return;

        // Store current visibility state before morphing
        const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
        const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

        // Morph the entire sticky bar content
        morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

        // Restore visibility state after morphing
        this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
        this.dataset.variantAvailable = variantAvailable;

        // Update the dataset attributes with new variant info
        if (variant && variant.id) {
          this.dataset.currentVariantId = variant.id;
        }

        // Re-cache the target add to cart button after morphing
        const productForm = this.#getProductForm();
        if (productForm) {
          this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
        }

        if (variant == null) {
          this.#handleVariantUnavailable();
        }
        // Restore the current quantity and synchronize the newly morphed
        // sticky quantity selector with the main product form.
        this.#syncSelectorValue(this.#getStickyQuantitySelector(), this.#currentQuantity);
        this.#syncSelectorValue(this.#getMainQuantitySelector(), this.#currentQuantity);
        this.#updateButtonText();
        requestAnimationFrame(() => this.#observeProductPriceStyles());
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Event promise rejected:', error);
      });
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CartLinesUpdateEvent | CartErrorEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    // Reset the puppet flag only after the cart operation's promise settles,
    // not when the event is first dispatched (before the HTTP request completes).
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) {
        this.#targetAddToCartButton.dataset.puppet = 'false';
      }
    };

    // CartLinesUpdateEvent has a promise; CartErrorEvent does not (error already happened).
    if ('promise' in event && event.promise instanceof Promise) {
      event.promise.finally(resetPuppet);
    } else {
      resetPuppet();
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to this product page's main/sticky selectors, never cart,
    // quick-add, or another product form rendered in a recommendation section.
    if (event.detail.cartLine) return;

    const source = event.target instanceof Element ? event.target.closest('quantity-selector-component') : null;
    const mainSelector = this.#getMainQuantitySelector();
    const stickySelector = this.#getStickyQuantitySelector();

    if (source !== mainSelector && source !== stickySelector) return;

    const quantity = Number(event.detail.quantity);
    if (!Number.isFinite(quantity) || quantity < 1) return;

    this.#currentQuantity = quantity;

    if (source === stickySelector) {
      this.#syncSelectorValue(mainSelector, quantity);
    } else {
      this.#syncSelectorValue(stickySelector, quantity);
    }

    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Checks whether the Shopify Chat is active on the page.
   * When active, the sticky bar must stay hidden to avoid overlapping the chat UI.
   *
   * <shopify-chat> is rendered unconditionally by chat-drawer.liquid, but
   * the "Ask anything" button only paints once the Inbox app has installed
   * and upgraded the element. Gate on the registration of the custom element
   * (the same signal chat-drawer.liquid uses via customElements.whenDefined)
   * so the inert placeholder on shops without Inbox doesn't suppress the
   * sticky bar.
   *
   * @returns {boolean}
   */
  #isChatActive() {
    if (!customElements.get('shopify-chat')) return false;
    return Boolean(document.querySelector('shopify-chat'));
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Returns the main product form quantity selector.
   * @returns {HTMLElement | null}
   */
  #getMainQuantitySelector() {
    return this.#getProductForm()?.querySelector('quantity-selector-component') ?? null;
  }

  /**
   * Returns the selector rendered inside the sticky bar.
   * @returns {HTMLElement | null}
   */
  #getStickyQuantitySelector() {
    return this.querySelector('.sticky-add-to-cart__quantity quantity-selector-component');
  }

  /**
   * Synchronize a quantity selector without dispatching another update event.
   * The component methods are intentionally feature-detected so the sticky bar
   * remains safe during section-rendering/custom-element upgrade races.
   * @param {HTMLElement | null} selector
   * @param {number} quantity
   */
  #syncSelectorValue(selector, quantity) {
    if (!selector) return;

    const input = selector.querySelector('input[ref="quantityInput"], input[name="quantity"]');
    if (input instanceof HTMLInputElement) {
      const min = Number.parseInt(input.min || '1', 10) || 1;
      const step = Number.parseInt(input.step || '1', 10) || 1;
      const componentMax =
        'getEffectiveMax' in selector && typeof selector.getEffectiveMax === 'function'
          ? selector.getEffectiveMax()
          : null;
      const attributeMax = input.max ? Number.parseInt(input.max, 10) : null;
      const max = componentMax ?? attributeMax ?? Number.POSITIVE_INFINITY;
      const bounded = Math.min(max, Math.max(min, quantity));
      const safeQuantity = min + Math.floor((bounded - min) / step) * step;

      input.value = safeQuantity.toString();
      this.#currentQuantity = safeQuantity;
    }

    if ('updateButtonStates' in selector && typeof selector.updateButtonStates === 'function') {
      selector.updateButtonStates();
    }
  }

  /**
   * Returns the visible product-price block that controls the product page
   * pricing appearance.
   * @returns {HTMLElement | null}
   */
  #getMainProductPrice() {
    const productId = this.dataset.productId;
    const sectionElement = this.closest('.shopify-section');
    if (!productId || !sectionElement) return null;

    return (
      sectionElement.querySelector(`.product-details product-price[data-product-id="${productId}"]`) ??
      sectionElement.querySelector(`product-price[data-product-id="${productId}"]`)
    );
  }

  /**
   * Copy the computed current/compare price colors and sizes from the main
   * product price block into CSS variables used by the sticky bar.
   */
  #syncProductPriceStyles = () => {
    this.#priceStyleFrame = null;
    const source = this.#getMainProductPrice();
    if (!source) return;

    const visiblePrice = Array.from(source.querySelectorAll('.price')).find((element) => {
      if (!(element instanceof HTMLElement)) return false;
      if (element.closest('.price__hidden')) return false;
      return getComputedStyle(element).display !== 'none';
    });

    const comparePrice = Array.from(source.querySelectorAll('.compare-at-price')).find((element) => {
      if (!(element instanceof HTMLElement)) return false;
      if (element.closest('.price__hidden')) return false;
      return getComputedStyle(element).display !== 'none';
    }) ?? source.querySelector('.compare-at-price');

    if (visiblePrice instanceof HTMLElement) {
      const style = getComputedStyle(visiblePrice);
      this.style.setProperty('--sticky-current-price-color', style.color);
      this.style.setProperty('--sticky-current-price-size', style.fontSize);
    }

    if (comparePrice instanceof HTMLElement) {
      const style = getComputedStyle(comparePrice);
      this.style.setProperty('--sticky-compare-price-color', style.color);
      this.style.setProperty('--sticky-compare-price-size', style.fontSize);
    }
  };

  /**
   * Observe the source price block so theme-editor styling changes and variant
   * price morphs stay synchronized without polling.
   */
  #observeProductPriceStyles() {
    this.#priceStyleObserver?.disconnect();
    const source = this.#getMainProductPrice();

    if (!source) {
      this.#priceStyleFrame = requestAnimationFrame(this.#syncProductPriceStyles);
      return;
    }

    this.#syncProductPriceStyles();
    this.#priceStyleObserver = new MutationObserver(() => {
      if (this.#priceStyleFrame !== null) cancelAnimationFrame(this.#priceStyleFrame);
      this.#priceStyleFrame = requestAnimationFrame(this.#syncProductPriceStyles);
    });
    this.#priceStyleObserver.observe(source, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    });
  }

  /**
   * Gets the initial quantity from the live product form when possible.
   */
  #getInitialQuantity() {
    const mainInput = this.#getMainQuantitySelector()?.querySelector('input[ref="quantityInput"], input[name="quantity"]');
    const mainQuantity = mainInput instanceof HTMLInputElement ? Number.parseInt(mainInput.value, 10) : Number.NaN;

    this.#currentQuantity = Number.isFinite(mainQuantity)
      ? mainQuantity
      : Number.parseInt(this.dataset.initialQuantity || '1', 10) || 1;

    this.#syncSelectorValue(this.#getStickyQuantitySelector(), this.#currentQuantity);
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
